/**
 * Art for the DOM UI, resolved ONLY through asset-manifest keys (ARCHITECTURE §9). The manifest contract lives in
 * src/app/asset-manifest.ts (type-only import: the UI never value-imports src/app). The UI loads the same
 * `/assets/manifest.json` once (the browser cache shares it with boot) and degrades gracefully when art is
 * missing: every <Art> has a fallback (nothing, or a styled placeholder).
 */
import { signal } from '@preact/signals';
import type { AssetManifest, ImageAsset } from '@hf/render-core/assets/manifest';

export type { AssetManifest, ImageAsset };

/** null = not loaded yet (or unavailable). */
export const manifest = signal<AssetManifest | null>(null);

let loading: Promise<void> | null = null;

/** Load the manifest once. Never throws; a missing manifest means "no art" (fallbacks render). */
export const ensureManifest = (url = '/assets/manifest.json'): Promise<void> => {
  if (manifest.value || loading) return loading ?? Promise.resolve();
  if (typeof fetch === 'undefined') return Promise.resolve();
  loading = fetch(url)
    .then(async (res) => {
      if (!res.ok) return;
      const json = (await res.json()) as AssetManifest;
      if (json && json.version === 1 && json.images) manifest.value = json;
    })
    .catch(() => undefined);
  return loading;
};

/** Tests / previews: inject a manifest directly. */
export const setManifest = (m: AssetManifest | null): void => {
  manifest.value = m;
  loading = null;
};

/** Key helpers (same conventions as src/app/asset-manifest.ts). */
export const headKey = (character: string, expression = 'neutral'): string =>
  `head/${character}/${expression}`;
export const gloveKey = (side: 'home' | 'away', pose: string): string => `glove/${side}/${pose}`;
export const iconKey = (specialKey: string): string => `icon/${specialKey}`;
/** A ball-skin icon for the Store's BALLS tab / in-match swap (handegg-roster.md "Ball skins rule"). */
export const ballSkinKey = (skin: string): string => `ball/${skin}`;
/** A coin-pack icon for the Store's COINS tab (design/exports/store.png). */
export const packKey = (pack: string): string => `pack/${pack}`;

/** First key present in the manifest (reads the signal: components re-render when it loads). */
export const resolveImage = (keys: readonly string[]): ImageAsset | null => {
  const images = manifest.value?.images;
  if (!images) return null;
  for (const k of keys) {
    const a = images[k];
    if (a) return a;
  }
  return null;
};

/** Head art with expression fallback to neutral (and determined as a last resort). */
export const headKeys = (character: string, expression = 'neutral'): string[] => [
  headKey(character, expression),
  headKey(character, 'neutral'),
  headKey(character, 'determined'),
];

/** Preload a set of images (character select warm-up) so swaps never flash. */
export const preloadImages = (keys: readonly string[]): void => {
  if (typeof Image === 'undefined') return;
  for (const k of keys) {
    const a = resolveImage([k]);
    if (!a) continue;
    const img = new Image();
    img.decoding = 'async';
    img.src = a.url;
  }
};
